/**
 * useTasks - the tasks attached to ONE opportunity/project row.
 *
 * An opportunity and a project are the same `projects` row, so a task keyed to
 * that row id is already there when the bid is won - tasks carry over exactly
 * like comments and Doc Hub, with no syncing. Storage is the crm_actions table
 * with entity_type='project'. (Tasks from the retired opportunity "Action
 * items" panel, entity_type='opportunity' on the same row id, are converted to
 * 'project' by schema_crm.sql / schema_ksa.sql at startup since 10/22/26.)
 */
import { useCallback, useEffect, useState } from 'react';
import { useApiBase, KSA_SCOPE } from '../../context/DataScope';
import { localDay } from './tasksTable';
import { subscribe } from '../../live/liveEvents';
import { createBatcher, latestReads } from '../../live/useLive';

export const TASK_ENTITY = 'project';

/**
 * GET the tasks: the rows, or null when they could not be read.
 * A refused or failed answer (a 429 or 502 while every open tab re-reads after
 * a change) is not "no tasks" - read as [] it used to empty the Tasks tab.
 */
const fetchTasks = (url) =>
  fetch(url)
    .then((r) => (r.ok ? r.json() : null))
    .then((rows) => (Array.isArray(rows) ? rows : null))
    .catch(() => null);
/**
 * Today as the LOCAL 'YYYY-MM-DD' - the same day the Tasks page and My tasks
 * use (tasksTable.localDay). toISOString's UTC day is tomorrow after about
 * 8pm Eastern, which made a task due today read overdue here only.
 */
export const todayISO = () => localDay(new Date());

/* ── keeping every view of a task in sync ──────────────────────────────────
 * The same task shows in up to three places at once: the Tasks tab, the Tasks
 * column behind it, and the Dashboard card. Each keeps its own copy, so a
 * delete used to update only the one you clicked in and the others went stale
 * until a page refresh. Every mutation now announces itself and they all
 * re-pull.
 *
 * Other people's task changes arrive the same way: while any of these
 * listeners is mounted, ONE live subscription (src/live) turns a matching
 * change into a single notifyTasksChanged per burst - so the Tasks page, My
 * tasks, the Leads board and every open Tasks tab pick it up with no refresh.
 *
 * Each listener is told why: { live: true } for someone else's change. Nobody
 * here is waiting for that re-read, so a view that cannot get it keeps its
 * list and shows no error; this tab's own changes pass nothing, and a view
 * may say when the re-read its person is waiting for fails. */
const listeners = new Set();
export const notifyTasksChanged = (why = {}) => {
  listeners.forEach((fn) => {
    try {
      fn(why);
    } catch {
      /* a dead listener must not stop the rest */
    }
  });
};
const LIVE = Object.freeze({ live: true });

/**
 * Can this live event have changed a task list? Tasks themselves (crm-actions,
 * here or on the GLOBAL KSA tab); a new opportunity, which arrives with its
 * first task; a deleted opportunity or account, which takes its tasks along.
 * Resync: always.
 */
export function touchesTasks(evt) {
  if (!evt) return false;
  if (evt.resync) return true;
  const scoped = evt.kind === KSA_SCOPE.key; // a GLOBAL KSA path: kind 'ksa', sub = what it is
  const kind = scoped ? evt.sub : evt.kind;
  if (kind === 'crm-actions') return true;
  if (kind === 'accounts') return evt.method === 'DELETE';
  if (kind !== 'projects' || (!scoped && evt.sub)) return false; // a record's notes and files are not tasks
  return (evt.method === 'POST' && !evt.id) || evt.method === 'DELETE';
}

let liveTasks = null; // { off, batch } while at least one listener is mounted
const holdLive = () => {
  if (liveTasks) return;
  const batch = createBatcher(() => notifyTasksChanged(LIVE));
  const off = subscribe((evt) => {
    if (touchesTasks(evt)) batch.add();
  });
  liveTasks = { off, batch };
};
const releaseLive = () => {
  if (!liveTasks || listeners.size) return;
  liveTasks.off();
  liveTasks.batch.stop();
  liveTasks = null;
};

export function useTasksChanged(onChange) {
  useEffect(() => {
    listeners.add(onChange);
    holdLive();
    return () => {
      listeners.delete(onChange);
      releaseLive();
    };
  }, [onChange]);
}

/** Newest-first, open before done - what "the latest task" means everywhere. */
export const sortTasks = (rows) =>
  [...rows].sort((a, b) => {
    const openA = a.status === 'open' ? 0 : 1;
    const openB = b.status === 'open' ? 0 : 1;
    if (openA !== openB) return openA - openB;
    return String(b.created_at || '').localeCompare(String(a.created_at || ''));
  });

export const isOverdue = (t) => t.status === 'open' && t.due_date && String(t.due_date).slice(0, 10) < todayISO();

/**
 * Every project task in one request, indexed by row id.
 *
 * The tables render hundreds of rows; a per-row fetch would be hundreds of
 * requests. The Tasks COLUMN reads this index, while the Tasks TAB uses
 * useTasks below for its own live copy of a single row.
 *
 * `version` is a counter — bump it to re-pull after a quick-add.
 *
 * Its reads can overlap (a quick-add's re-pull, someone else's change), so
 * they go through latestReads: the newest answer GOT is shown, an older one
 * landing late never is, and one that could not be read changes nothing. (A
 * newer read used to cancel the one before it, so when that newer one failed
 * the column kept neither.)
 */
export function useProjectTasksIndex(version = 0) {
  const base = useApiBase(); // the KSA tab swaps in its own prefix (context/DataScope.js)
  const [index, setIndex] = useState(() => new Map());
  const [tick, setTick] = useState(0);
  const [reads] = useState(latestReads);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  useTasksChanged(reload);

  useEffect(() => {
    const ticket = reads.start();
    fetchTasks(`${base}/crm-actions?entity_type=${TASK_ENTITY}`).then((list) => {
      if (!list || !reads.show(ticket)) return; // could not read: the column stays as it is
      const m = new Map();
      for (const t of list) {
        if (!m.has(t.entity_id)) m.set(t.entity_id, []);
        m.get(t.entity_id).push(t);
      }
      for (const [k, v] of m) m.set(k, sortTasks(v));
      setIndex(m);
    });
  }, [version, tick, base, reads]);

  return index;
}

export function useTasks(projectId, { enabled = true } = {}) {
  const base = useApiBase();
  const [tasks, setTasks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [reads] = useState(latestReads); // its load, a re-pull after a change here, a live re-pull

  const load = useCallback(() => {
    if (!projectId || !enabled) return;
    const id = encodeURIComponent(projectId);
    const ticket = reads.start();
    fetchTasks(`${base}/crm-actions?entity_type=${TASK_ENTITY}&entity_id=${id}`).then((list) => {
      // could not read: the tasks on screen stay (a first load still stops saying "Loading")
      if (list && reads.show(ticket)) setTasks(sortTasks(list));
      setLoaded(true);
    });
  }, [projectId, enabled, base, reads]);

  // another row: its own tasks only - a read still out for the last one never lands here
  useEffect(() => {
    reads.show(reads.start());
    setTasks([]);
    setLoaded(false);
    load();
  }, [load, reads]);
  useTasksChanged(load);

  // contact_id (T-02) is sent only when the form had a Contact picker; tagged
  // (the people tagged besides the owner) only when the form sent it
  const add = useCallback(
    async ({ description, owner, due_date, entityLabel, contact_id, tagged }) => {
      const r = await fetch(`${base}/crm-actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description,
          owner,
          due_date,
          entity_type: TASK_ENTITY,
          entity_id: projectId,
          entity_label: entityLabel,
          ...(contact_id !== undefined ? { contact_id } : {}),
          ...(tagged !== undefined ? { tagged } : {}),
        }),
      });
      if (r.ok) notifyTasksChanged();
      return r.ok;
    },
    [projectId, base]
  );

  /** Edit a task in place. Changing your mind used to mean delete-and-retype. */
  const update = useCallback(
    async ({ id, description, owner, due_date, contact_id, tagged }) => {
      const r = await fetch(`${base}/crm-actions/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description,
          owner,
          due_date,
          ...(contact_id !== undefined ? { contact_id } : {}),
          ...(tagged !== undefined ? { tagged } : {}),
        }),
      });
      if (r.ok) notifyTasksChanged();
      return r.ok;
    },
    [base]
  );

  const setStatus = useCallback(
    async (id, status) => {
      await fetch(`${base}/crm-actions/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      notifyTasksChanged();
    },
    [base]
  );

  const remove = useCallback(
    async (id) => {
      // drop it locally first so it vanishes immediately - newer than any read
      // still on its way, which would put it back - then tell every other view
      reads.show(reads.start());
      setTasks((cur) => cur.filter((t) => t.id !== id));
      await fetch(`${base}/crm-actions/${id}`, { method: 'DELETE' });
      notifyTasksChanged();
    },
    [base, reads]
  );

  const open = tasks.filter((t) => t.status === 'open');
  return { tasks, open, loaded, load, add, update, setStatus, remove };
}
